// driving_school_classroom_multi_enroll_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {

  static targets = [
    "checkbox",
    "pupilRow",
    "enrolledList",
    "attendanceCount",
    "error",
    "saveButton",
    "searchInput",
    "noResults",
    "results",
    "loading",
    "invalidError",
    "invalidErrorText",
    "invalidErrorLink"
  ]
  static values = {
    programId: String
  }

  connect() {
    this.searchSeq = 0
    // Populate a starter list (blank search returns the first students by name).
    this.search("")
  }

  // --- Search (server-backed, replaces the old preloaded roster) --------------

  filterPupils(event) {
    const term = event?.target?.value || ""
    clearTimeout(this.searchDebounce)
    this.searchDebounce = setTimeout(() => this.search(term), 250)
  }

  search(term) {
    const seq = ++this.searchSeq
    if (this.hasLoadingTarget) this.loadingTarget.hidden = false
    if (this.hasNoResultsTarget) this.noResultsTarget.hidden = true

    const url = `/schools/classroom/program/${this.programIdValue}/quick-look/enroll/search?search=${encodeURIComponent(term)}`

    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
      .then(response => {
        if (!response.ok) throw new Error('Search failed')
        return response.json()
      })
      .then(data => {
        // Ignore stale responses that arrive out of order.
        if (seq !== this.searchSeq) return
        this.renderResults(data.results || [])
      })
      .catch(error => {
        if (seq !== this.searchSeq) return
        console.error('Error:', error)
        if (this.hasResultsTarget) this.resultsTarget.innerHTML = ""
      })
      .finally(() => {
        if (seq === this.searchSeq && this.hasLoadingTarget) this.loadingTarget.hidden = true
      })
  }

  renderResults(results) {
    if (!this.hasResultsTarget) return

    // Don't offer students already in the attending list (freshly added ones
    // aren't in the DB yet, so the server can't have excluded them).
    const enrolledIds = this.enrolledPupilIds()
    const rows = results.filter(pupil => !enrolledIds.includes(String(pupil.id)))

    this.resultsTarget.innerHTML = rows.map(pupil => this.pupilRowHtml(pupil)).join("")

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.hidden = rows.length > 0
    }
  }

  pupilRowHtml(pupil) {
    const image = pupil.image || 'person_default_page.jpg'
    return `
      <div
        id="pupil-row-${pupil.id}"
        class="u-borderBottom py05 mt05"
        data-driving-school-classroom-multi-enroll-target="pupilRow"
        data-pupil-name="${this.escapeHtml(pupil.name)}"
        data-pupil-image="${this.escapeHtml(image)}"
        data-details-url="${this.escapeHtml(pupil.details_url || '#')}">
        <div class="Input--checkbox u-block">
          <input class="Input--checkbox-input"
            type="checkbox"
            id="driving_school_classroom_session_attendances_${pupil.id}"
            name="driving_school_classroom_session_attendances[]"
            value="${pupil.id}"
            data-action="change->driving-school-classroom-multi-enroll#toggleEnrollment"
            data-driving-school-classroom-multi-enroll-target="checkbox">
          <label class="Input--checkbox-label"
            for="driving_school_classroom_session_attendances_${pupil.id}">
            <span class="media-body">
              <span class="u-medium u-block">${this.escapeHtml(pupil.name)}</span>
            </span>
            <span class="media-right">
              <img src="${this.escapeHtml(image)}" width="30" class="media-object u-img u-circleImage" alt="${this.escapeHtml(pupil.name)}">
            </span>
          </label>
        </div>
      </div>
    `
  }

  // --- Selecting a student (validate the package before adding) ---------------

  toggleEnrollment(event) {
    const checkbox = event.target
    const pupilRow = checkbox.closest('[data-driving-school-classroom-multi-enroll-target="pupilRow"]')

    if (!pupilRow) {
      console.error('Could not find pupil row', checkbox)
      return
    }

    const pupilId = pupilRow.id.replace('pupil-row-', '')

    if (!checkbox.checked) {
      this.removeFromAttending(pupilId)
      return
    }

    // Checked: validate this one student has the right package before adding.
    checkbox.disabled = true
    this.validatePupil(pupilId)
      .then(data => {
        if (data.eligible) {
          this.addToAttending(pupilId, pupilRow)
        } else {
          checkbox.checked = false
          this.showInvalidError(data.name, data.details_url)
        }
      })
      .catch(error => {
        console.error('Error:', error)
        checkbox.checked = false
        this.errorTarget.hidden = false
      })
      .finally(() => {
        checkbox.disabled = false
      })
  }

  validatePupil(pupilId) {
    const url = `/schools/classroom/program/${this.programIdValue}/quick-look/enroll/validate?pupil_id=${encodeURIComponent(pupilId)}`
    return fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    }).then(response => {
      if (!response.ok) throw new Error('Validation failed')
      return response.json()
    })
  }

  addToAttending(pupilId, pupilRow) {
    const pupilName = pupilRow.dataset.pupilName || 'Unknown'
    const pupilImage = pupilRow.dataset.pupilImage || 'person_default_page.jpg'
    const attendeeHtml = this.createAttendeeHtml(pupilId, pupilName, pupilImage)
    this.enrolledListTarget.insertAdjacentHTML('beforeend', attendeeHtml)
    this.updateAttendanceCount(1)
  }

  removeFromAttending(pupilId) {
    const enrolledRow = this.enrolledListTarget.querySelector(`#pupil-row-${pupilId}`)
    if (enrolledRow) {
      enrolledRow.remove()
      this.updateAttendanceCount(-1)
    }
  }

  showInvalidError(name, detailsUrl) {
    if (!this.hasInvalidErrorTarget) return
    this.invalidErrorTextTarget.textContent =
      `${name || 'This student'} cannot be added because they do not have this classroom package. `
    if (this.hasInvalidErrorLinkTarget) {
      this.invalidErrorLinkTarget.href = detailsUrl || '#'
    }
    this.invalidErrorTarget.hidden = false
  }

  dismissInvalidError(event) {
    if (event) event.preventDefault()
    if (this.hasInvalidErrorTarget) this.invalidErrorTarget.hidden = true
  }

  enrolledPupilIds() {
    return Array.from(this.enrolledListTarget.children)
      .map(row => row.id.replace('pupil-row-', ''))
      .filter(Boolean)
  }

  // --- Remove / save (unchanged behaviour) ------------------------------------

  remove(event) {
    event.preventDefault();

    const pupilId = event.currentTarget.getAttribute('data-pupil-id');

    fetch(`/schools/classroom/program/${this.programIdValue}/quick-look/remove/${pupilId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok')
          }
          return response.text()
        })
        .then(html => {
          Turbo.renderStreamMessage(html)
        })
        .catch(error => {
          this.errorTarget.hidden = false
          console.error('Error:', error)
          this.saveButtonTarget.disabled = false
        })

  }


  removeMakeUpAttendance(event) {
    event.preventDefault();

    const pupilId = event.currentTarget.getAttribute('data-pupil-id');

    fetch(`/schools/classroom/program/${this.programIdValue}/quick-look/remove-single/${pupilId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.text()
    })
    .then(html => {
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      this.errorTarget.hidden = false
      console.error('Error:', error)
    })

  }

  saveEnrollments(event) {
    event.preventDefault()

    if (this.isSavingEnrollments) {
      return
    }

    this.isSavingEnrollments = true
    let enrolledPupilIds = this.enrolledPupilIds()

    this.saveButtonTarget.disabled = true

    fetch(`/schools/classroom/program/${this.programIdValue}/quick-look/enroll`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ pupil_ids: enrolledPupilIds }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.text()
    })
    .then(html => {
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      this.isSavingEnrollments = false
      this.errorTarget.hidden = false
      console.error('Error:', error)
      this.saveButtonTarget.disabled = false
    })
  }

  createAttendeeHtml(pupilId, pupilName, pupilImage) {
    return `
      <div id="pupil-row-${pupilId}" class="">
        <div class="u-alignMiddle pb05">
          <span class="u-flex u-flexAlignCenter">
            <span class="pr1">
              <img src="${this.escapeHtml(pupilImage)}" width="24" class="media-object u-img u-circleImage" alt="${this.escapeHtml(pupilName)}">
            </span>
            <span class="u-alignMiddle">
              ${this.escapeHtml(pupilName)}
            </span>
          </span>
        </div>
      </div>
    `
  }

  updateAttendanceCount(change) {
    if (this.hasAttendanceCountTarget) {
      const currentCount = parseInt(this.attendanceCountTarget.textContent, 10)
      const newCount = currentCount + change
      this.attendanceCountTarget.textContent = newCount
    }
  }

  escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }
}
